import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { FOCUS_KEYS, type TimerSnapshot } from "../../api/focus";
import { isBreak } from "../focus";
import {
  claimAlert,
  holdAlertLeadership,
  showNotification,
  sweepAlertKeys,
} from "../focusNotify";
import { playChime } from "../focusSound";
import { formatClock } from "../focusTime";
import type { FocusPrefs } from "./useFocusPrefs";

/** After 0:00, ask again until the server has advanced the phase. */
const WRAP_UP_RETRIES_MS = [1_500, 3_000, 6_000, 12_000];
/** A phase that ended within this much of its planned end ran out (vs. aborted/skipped). */
const NATURAL_END_SLACK_MS = 2_000;

interface Args {
  snapshot: TimerSnapshot | undefined;
  remainingMs: number | null;
  prefs: FocusPrefs;
}

/**
 * Everything a phase change does outside the page: the wrap-up refetches at
 * 0:00, the tab-title countdown, and (opt-in) the notification + chime.
 * Mounted once — by `FocusTimerChip` — so the effects never run twice.
 */
export function useFocusPhaseEffects({ snapshot, remainingMs, prefs }: Args): void {
  const qc = useQueryClient();
  const session = snapshot && snapshot.phase !== "idle" ? snapshot.session : null;

  // 1. Wrapping up: the server materializes the next phase at planned_end_at;
  //    a live frame normally announces it, these refetches cover a lost one.
  const expiredSession = session && remainingMs !== null && remainingMs <= 0 ? session.id : null;
  useEffect(() => {
    if (!expiredSession) return;
    const timers = WRAP_UP_RETRIES_MS.map((ms) =>
      window.setTimeout(() => void qc.refetchQueries({ queryKey: FOCUS_KEYS.timer }), ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [expiredSession, qc]);

  // 2. Tab title: "● 18:42 Focus · Jarvis" / "Break 03:10 · Jarvis".
  let title: string | null = null;
  if (session && snapshot && prefs.titleCountdown && remainingMs !== null) {
    const clock = remainingMs <= 0 ? "0:00" : formatClock(Math.ceil(remainingMs / 1000));
    title = isBreak(snapshot.phase) ? `Break ${clock} · Jarvis` : `● ${clock} Focus · Jarvis`;
  }
  const baseTitle = useRef<string | null>(null);
  useEffect(() => {
    if (title) {
      if (baseTitle.current === null) baseTitle.current = document.title;
      document.title = title;
    } else if (baseTitle.current !== null) {
      document.title = baseTitle.current;
      baseTitle.current = null;
    }
  }, [title]);
  useEffect(
    () => () => {
      if (baseTitle.current !== null) document.title = baseTitle.current;
    },
    [],
  );

  // 3. Alerts — only for a phase that ran out (not an abort or skip), only
  //    in the tab holding the alert lock, once per (ended session, new phase).
  const wantsAlerts = prefs.notify || prefs.sound;
  const leader = useRef(false);
  useEffect(() => {
    if (!wantsAlerts) return;
    sweepAlertKeys(Date.now());
    return holdAlertLeadership((isLeader) => {
      leader.current = isLeader;
    });
  }, [wantsAlerts]);

  const previous = useRef<TimerSnapshot | undefined>(undefined);
  const prefsRef = useRef(prefs);
  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);
  useEffect(() => {
    const before = previous.current;
    previous.current = snapshot;
    const ended = before && before.phase !== "idle" ? before.session : null;
    if (!snapshot || !ended || snapshot.session?.id === ended.id) return;
    if (snapshot.phase === "focus") return; // a new focus was started by hand
    const endedAt = Date.parse(ended.planned_end_at);
    if (Date.parse(snapshot.server_now) < endedAt - NATURAL_END_SLACK_MS) return;
    const { notify, sound } = prefsRef.current;
    if (!(notify || sound) || !leader.current) return;
    const key = `${ended.id}:${snapshot.phase}`;
    if (!claimAlert(key)) return;
    if (notify) {
      const [heading, body] = alertText(snapshot, ended.goal_title);
      showNotification(key, heading, body);
    }
    if (sound) playChime(snapshot.phase === "idle" ? "break_end" : "focus_end");
  }, [snapshot]);
}

function alertText(next: TimerSnapshot, goalTitle: string | null): [string, string] {
  const { settings } = next;
  if (next.phase === "long_break") {
    return ["Focus done – long break", `Take ${settings.long_break_minutes} minutes.`];
  }
  if (next.phase === "short_break") {
    const what = goalTitle ? `${goalTitle}: ` : "";
    return ["Focus done", `${what}take a ${settings.short_break_minutes}-minute break.`];
  }
  return ["Break over", "Ready for the next focus."];
}
